import type { Condition } from '../../../../types/segments/segments.types';
import type {
  EventAttributeCondition,
  EventAttributeFilter,
  EventAttributeLogicalOperator,
} from '../../../../types/events.types';

const EVENT_FIELD_PREFIX = 'event.';

export function eventFieldFromAttribute(attribute: string): string {
  if (!attribute.trim()) return '';
  return `${EVENT_FIELD_PREFIX}${attribute.trim()}`;
}

const EVENT_EMPTY_FIELD = 'event.';

export function attributeFromEventField(
  field: string | undefined
): string | null {
  if (field === undefined || field === null) return null;
  if (typeof field !== 'string') return null;
  if (field === '' || field === EVENT_EMPTY_FIELD) return '';
  if (!field.startsWith(EVENT_FIELD_PREFIX)) return null;
  return field.slice(EVENT_FIELD_PREFIX.length).trim();
}

const LOGICAL_MAP: Record<EventAttributeLogicalOperator, 'and' | 'or'> = {
  all: 'and',
  any: 'or',
};

const LOGICAL_MAP_REVERSE = { and: 'all' as const, or: 'any' as const };

function conditionValueToString(value: Condition['value']): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const v = value as Record<string, unknown>;
    if (typeof v.datetime === 'string') return v.datetime;
    if (v.datetime instanceof Date) return v.datetime.toISOString();
    if (typeof v.date === 'string') return v.date;
    if (typeof v.start === 'string') return v.start;
    if (typeof v.end === 'string') return `${v.start ?? ''}-${v.end}`;
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
}

function flattenConditionsToEventAttributeList(
  conditions: Condition[]
): EventAttributeCondition[] {
  const list: EventAttributeCondition[] = [];
  for (const c of conditions) {
    if (c.condition === 'group') {
      const groupValue = c.value as { conditions?: Condition[] } | undefined;
      const nested = groupValue?.conditions ?? [];
      list.push(...flattenConditionsToEventAttributeList(nested));
      continue;
    }
    const attribute = attributeFromEventField(c.field);
    if (attribute === null) continue;
    const value = conditionValueToString(c.value);
    list.push({
      attribute,
      operator: c.operator || 'equals',
      value,
    });
  }
  return list;
}

export function eventAttributeFilterFromConditions(conditionsRecord: {
  [key: string]: Condition[];
}): EventAttributeFilter {
  const key =
    Object.keys(conditionsRecord).find((k) => k === 'and' || k === 'or') ||
    'and';
  const conditions = conditionsRecord[key] ?? [];
  const logicalOperator = LOGICAL_MAP_REVERSE[key as 'and' | 'or'] ?? 'all';

  const list = flattenConditionsToEventAttributeList(conditions);

  return { logicalOperator, conditions: list };
}

function eventFieldForAttribute(attribute: string): string {
  return attribute.trim()
    ? eventFieldFromAttribute(attribute)
    : EVENT_EMPTY_FIELD;
}

function parseAtLeastOneValue(
  s: string
): { value: string; operator: string } | null {
  if (typeof s !== 'string' || !s.trim()) return null;
  try {
    const o = JSON.parse(s) as Record<string, unknown>;
    if (o && typeof o.operator === 'string' && 'value' in o)
      return { value: String(o.value), operator: o.operator };
  } catch {}
  return null;
}

export function conditionsFromEventAttributeFilter(
  filter: EventAttributeFilter
): { [key: string]: Condition[] } {
  const key = LOGICAL_MAP[filter.logicalOperator];
  const conditions: Condition[] = filter.conditions.map((c) => {
    const base = {
      field: eventFieldForAttribute(c.attribute),
      operator: c.operator,
    };
    if (
      ['exists', 'not_exists', 'is_empty', 'is_not_empty'].includes(c.operator)
    )
      return { ...base, value: '' };
    if (c.operator === 'at_least_one') {
      const parsed = parseAtLeastOneValue(c.value ?? '');
      if (parsed)
        return {
          ...base,
          value: { value: parsed.value, operator: parsed.operator },
          arrayType: 'arrayValue',
        };
    }
    return { ...base, value: c.value ?? '' };
  });
  return { [key]: conditions };
}
